"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoundingApproach = void 0;
const invariant_1 = require("@turf/invariant");
const object_hash_1 = __importDefault(require("object-hash"));
const react_1 = __importDefault(require("react"));
const utils_1 = require("../../../lib/utils");
const utils_2 = require("../../utils");
const map_1 = require("../map");
const util_1 = require("../util");
const FabricMapClusters_1 = require("./FabricMapClusters");
const FabricMapClusters_2 = require("./FabricMapClusters/FabricMapClusters");
const FabricMapLayer_helpers_1 = require("./FabricMapLayer.helpers");
var RoundingApproach;
(function (RoundingApproach) {
    /**
     * Provides no rounding. Floats will stay floats.
     */
    RoundingApproach["NONE"] = "none";
    /**
     * Rounds up/down to the nearest integer.
     */
    RoundingApproach["NORMAL"] = "normal";
    /**
     * Rounds up to the nearest integer.
     */
    RoundingApproach["UP"] = "ceil";
    /**
     * Rounds down to the nearest integer.
     */
    RoundingApproach["DOWN"] = "floor";
    /**
     * Rounds to the number of significant figures specified
     */
    RoundingApproach["SIGNIFICANT_FIGURES"] = "significant_figures";
})(RoundingApproach = exports.RoundingApproach || (exports.RoundingApproach = {}));
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => { };
const FabricMapLayerRaw = react_1.default.memo((props) => {
    const { before, beforeNonFabricLayer, cluster, disableHighlight, highlighted, id, invert, layer, map, onMouseClick, onMouseEnter, onMouseLeave, onMouseMove, outlinePaint, paint, labelingProperties, } = props;
    (0, util_1.useWhy)("Layer", props);
    /**
     * Either use a user-provided id or hash all the serializable props to make an id.
     *
     * We use hash instead of a UUID so that in React.StrictMode we aren't creating multiple layers.
     * `props` isn't included in deps because we want this id to stay the same when props change.
     */
    const idPrefix = react_1.default.useMemo(() => id ||
        (0, object_hash_1.default)({
            before,
            cluster,
            disableHighlight,
            highlighted,
            id,
            invert,
            layer,
            onMouseClick,
            onMouseEnter,
            onMouseLeave,
            onMouseMove,
            outlinePaint,
            paint,
            labelingProperties,
        }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]);
    // // ` added to deal with Chrome dev tools bug
    const MAP_LAYER_SOURCE = `${idPrefix}-fabric-map-layer-source`; // `
    const MAP_LAYER = `${idPrefix}-fabric-map-layer`; // `
    const MAP_OUTLINE = `${idPrefix}-fabric-map-outline`; // `
    const LABEL_LAYER = `${idPrefix}-fabric-map-polygon-label`;
    const { clusterMaxZoom } = react_1.default.useMemo(() => (0, FabricMapClusters_2.getClusterOptions)(cluster), [cluster]);
    /**
     * Either the `layer` prop or that layer inverted, if `inverted` prop.
     */
    const trueLayer = react_1.default.useMemo(() => (!invert ? layer : (0, FabricMapLayer_helpers_1.makeOutlinePolygon)(layer)), [invert, layer]);
    /**
     * If clustering is enabled, these layers will be hidden when clusters are present
     */
    const layersToHideWhenClustering = react_1.default.useMemo(() => [MAP_LAYER, MAP_OUTLINE], [MAP_LAYER, MAP_OUTLINE]);
    /**
     * Passes cluster config to the clustering component
     */
    const clusterProps = react_1.default.useMemo(() => (cluster && typeof cluster === "object" ? cluster : {}), [cluster]);
    const beforeId = react_1.default.useMemo(() => 
    // Only append string if the user hasn't told us not to.
    before && !beforeNonFabricLayer ? `${before}-fabric-map-layer` : before, [before, beforeNonFabricLayer]);
    const hovered = react_1.default.useRef();
    const destroyRef = react_1.default.useRef(noop);
    const setFeatureState = react_1.default.useCallback((hoverZoneID, stateName, setTo) => {
        map.setFeatureState({
            source: MAP_LAYER_SOURCE,
            id: hoverZoneID,
        }, { [stateName]: setTo });
    }, [map, MAP_LAYER_SOURCE]);
    const onLeave = react_1.default.useCallback((e) => {
        if (hovered.current) {
            if (!disableHighlight) {
                setFeatureState(hovered.current.id, "hover", false);
            }
            hovered.current = undefined;
            if (onMouseLeave) {
                onMouseLeave(hovered.current, e);
            }
        }
    }, [onMouseLeave, setFeatureState, disableHighlight]);
    const onMove = react_1.default.useCallback((e) => {
        var _a;
        const feature = e.features[0];
        if (onMouseMove) {
            onMouseMove(feature, e);
        }
        if (((_a = hovered.current) === null || _a === void 0 ? void 0 : _a.id) !== (feature === null || feature === void 0 ? void 0 : feature.id)) {
            onLeave(e);
            if (feature) {
                if (!disableHighlight) {
                    hovered.current = feature;
                    setFeatureState(hovered.current.id, "hover", true);
                }
                if (onMouseEnter) {
                    onMouseEnter(feature, e);
                }
            }
        }
    }, [onMouseMove, onLeave, disableHighlight, onMouseEnter, setFeatureState]);
    const onClick = react_1.default.useCallback((e) => {
        const feature = e.features[0];
        if (onMouseClick) {
            onMouseClick(feature, e);
        }
        e.originalEvent.cancelBubble = true;
    }, [onMouseClick]);
    react_1.default.useLayoutEffect(() => {
        if (highlighted) {
            highlighted.forEach((id) => setFeatureState(id, "selected", true));
        }
        return () => {
            if (highlighted) {
                highlighted.forEach((id) => setFeatureState(id, "selected", false));
            }
        };
    }, [highlighted, setFeatureState]);
    /**
     * Rendering the layer
     */
    react_1.default.useMemo(() => {
        utils_2.featureIdGenerator.updateFeatureIds(trueLayer);
        if (labelingProperties === null || labelingProperties === void 0 ? void 0 : labelingProperties.labelProperty) {
            // round numbers to the nearest whole number
            utils_2.numericLabelRounding.roundNumericLabelText(trueLayer, labelingProperties.labelProperty, labelingProperties.roundingApproach);
        }
        (0, FabricMapLayer_helpers_1.addOrUpdateMapLayerSource)(MAP_LAYER_SOURCE, trueLayer, map);
        const customPaint = (0, utils_1.isFunction)(paint) ? paint(trueLayer) : paint;
        const customOutlinePaint = (0, utils_1.isFunction)(outlinePaint)
            ? outlinePaint(trueLayer)
            : outlinePaint;
        /**
         * The layers frequently re-render, so we need to enforce their visibility
         */
        const layerVisibility = map.getZoom() <= clusterMaxZoom ? "none" : "visible";
        /**
         * I'm not proud of this approach, but when tried to use `getType(layer) === "Point"` condition inside `type` and `paint` to change only those values, for some reason I didn't find `addOrUpdateMapLayer` method was identifying layer as `SkyMap` type.
         *
         * I also tried to create an object outside the method and change only the attributes with condition after object creation in order to avoid so much repeated code, but it was getting similar errors
         */
        if ((0, invariant_1.getType)(layer) === "Point") {
            (0, FabricMapLayer_helpers_1.addOrUpdateMapLayer)({
                layer: {
                    id: MAP_LAYER,
                    type: "circle",
                    source: MAP_LAYER_SOURCE,
                    layout: {
                        visibility: layerVisibility,
                    },
                    paint: Object.assign({ "circle-radius": 6, "circle-color": "#336699", "circle-opacity": 1, "circle-stroke-color": "#eee", "circle-stroke-width": 1 }, customPaint),
                },
                beforeId,
                map,
            });
        }
        else {
            (0, FabricMapLayer_helpers_1.addOrUpdateMapLayer)({
                layer: {
                    id: MAP_LAYER,
                    type: "fill",
                    source: MAP_LAYER_SOURCE,
                    layout: {
                        visibility: layerVisibility,
                    },
                    paint: Object.assign({ 
                        /**
                         * Inverted layers are grey overlay
                         */
                        "fill-color": !invert ? "#f2f2f2" : "#000", "fill-antialias": true, 
                        // The feature-state dependent fill-opacity expression will render the hover effect
                        // when a feature's hover state is set to true.
                        "fill-opacity": !invert
                            ? [
                                "case",
                                ["boolean", ["feature-state", "hover"], false],
                                0.9,
                                ["boolean", ["feature-state", "selected"], false],
                                0.9,
                                0.25,
                            ]
                            : 0.25, "fill-outline-color": "#ffffff" }, customPaint),
                },
                beforeId,
                map,
            });
        }
        (0, FabricMapLayer_helpers_1.addOrUpdateMapLayer)({
            layer: {
                id: MAP_OUTLINE,
                type: "line",
                source: MAP_LAYER_SOURCE,
                layout: {
                    visibility: layerVisibility,
                },
                paint: Object.assign({ "line-color": "#ffffff", "line-width": [
                        "case",
                        ["boolean", ["feature-state", "selected"], false],
                        3,
                        1,
                    ] }, customOutlinePaint),
            },
            beforeId,
            map,
        });
        if (labelingProperties === null || labelingProperties === void 0 ? void 0 : labelingProperties.labelProperty) {
            (0, FabricMapLayer_helpers_1.addOrUpdateMapLayer)({
                layer: {
                    id: LABEL_LAYER,
                    type: "symbol",
                    source: MAP_LAYER_SOURCE,
                    layout: {
                        "text-field": ["get", labelingProperties.labelProperty],
                        "text-font": ["Avenir LT Pro Bold", "Arial Unicode MS Regular"],
                        "text-offset": [0, 0],
                        "text-size": labelingProperties.textSize,
                        "text-anchor": "center",
                        "text-keep-upright": true,
                    },
                    paint: {
                        "text-color": labelingProperties.textColor,
                        "text-halo-color": labelingProperties["haloColor"]
                            ? labelingProperties.haloColor
                            : labelingProperties.textColor,
                        "text-halo-width": labelingProperties["haloSize"]
                            ? labelingProperties.haloSize
                            : 0,
                    },
                },
                map,
            });
        }
        destroyRef.current = () => {
            utils_2.featureIdGenerator.resetFeatureIds(trueLayer);
            if (map && map.getStyle()) {
                if (map.getLayer(LABEL_LAYER)) {
                    map.removeLayer(LABEL_LAYER);
                }
                if (map.getLayer(MAP_OUTLINE)) {
                    map.removeLayer(MAP_OUTLINE);
                }
                if (map.getLayer(MAP_LAYER)) {
                    map.removeLayer(MAP_LAYER);
                }
                map.removeSource(MAP_LAYER_SOURCE);
            }
        };
    }, [
        trueLayer,
        labelingProperties,
        MAP_LAYER_SOURCE,
        map,
        paint,
        outlinePaint,
        clusterMaxZoom,
        layer,
        MAP_OUTLINE,
        beforeId,
        MAP_LAYER,
        invert,
        LABEL_LAYER,
    ]);
    react_1.default.useEffect(() => () => destroyRef.current(), []);
    react_1.default.useLayoutEffect(() => {
        if (onMouseClick) {
            map.on("click", MAP_LAYER, onClick);
        }
        if (onMouseEnter || onMouseLeave || !disableHighlight) {
            map.on("mousemove", MAP_LAYER, onMove);
            map.on("mouseleave", MAP_LAYER, onLeave);
        }
        return () => {
            map.off("click", MAP_LAYER, onClick);
            map.off("mouseleave", MAP_LAYER, onLeave);
            map.off("mousemove", MAP_LAYER, onMove);
        };
    }, [
        disableHighlight,
        idPrefix,
        MAP_LAYER,
        map,
        onClick,
        onLeave,
        onMouseClick,
        onMouseEnter,
        onMouseLeave,
        onMove,
    ]);
    return react_1.default.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null, cluster && (react_1.default.createElement(FabricMapClusters_1.FabricMapClusters, Object.assign({}, clusterProps, { idPrefix: idPrefix, layersToHideWhenClustering: layersToHideWhenClustering, map: map, layer: layer }))))), [cluster, clusterProps, idPrefix, layer, layersToHideWhenClustering, map]);
});
FabricMapLayerRaw.displayName = "FabricMapLayer";
/**
 * **🚨 FabricMapLayer is extremely susceptible to accidental re-renders. 🚨**
 *
 * As a matter of best practice, you should be memoizing everything you pass into the map.
 * At the very least, make sure all your functions parameters are memoized, because they're
 * often reinitialized inside your components. You can spot your un-memoized parameters by
 * wrapping your whole render in `React.useMemo`.
 */
const FabricMapLayer = (0, map_1.withMap)(FabricMapLayerRaw);
exports.default = FabricMapLayer;
